import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PendingIcon from '@mui/icons-material/Pending';

const ScraperStatus = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      loadStatus();
    }
  }, [open]);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/scraper/status');
      if (response.data.success) {
        setData(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <PlayArrowIcon color="primary" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Scraper Status</Typography>
          <Button size="small" onClick={loadStatus} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load scraper status: {error}
          </Alert>
        )}

        {!loading && data && (
          <>
            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                      Total Tasks
                    </Typography>
                    <Typography variant="h4">
                      {data.stats.total_tasks || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                      Running
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {data.stats.running_tasks || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                      Completed
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {data.stats.completed_tasks || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                      Failed
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {data.stats.failed_tasks || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={6}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                      Total Items
                    </Typography>
                    <Typography variant="h4">
                      {data.stats.total_items || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={6}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                      Success Items
                    </Typography>
                    <Typography variant="h4">
                      {data.stats.total_success || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tabs */}
            <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tab label="Tasks" />
              <Tab label="Logs" />
            </Tabs>

            {/* Tasks Table */}
            {currentTab === 0 && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Processed</TableCell>
                      <TableCell align="right">Success</TableCell>
                      <TableCell align="right">Failed</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.tasks.map((task) => (
                      <TableRow key={task.id} hover>
                        <TableCell>{task.id}</TableCell>
                        <TableCell>
                          <Chip label={task.task_type} size="small" />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusIcon(task.status)}
                            <Chip
                              label={task.status}
                              color={getStatusColor(task.status)}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">{task.items_total || 0}</TableCell>
                        <TableCell align="right">{task.items_processed || 0}</TableCell>
                        <TableCell align="right">{task.items_success || 0}</TableCell>
                        <TableCell align="right">{task.items_failed || 0}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(task.started_at)}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(task.completed_at)}</TableCell>
                        <TableCell>
                          {task.error_message && (
                            <Typography variant="caption" color="error">
                              {task.error_message.substring(0, 50)}...
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Logs Table */}
            {currentTab === 1 && (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Task ID</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.id}</TableCell>
                        <TableCell>{log.task_id}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.level}
                            color={log.level === 'error' ? 'error' : log.level === 'warning' ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {log.message}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(log.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScraperStatus;
